import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/lookInside';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, insidePages.length - 1));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (slide) {
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const children = Array.from(track.children) as HTMLElement[];
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let closestDistance = Infinity;
        children.forEach((child, i) => {
          const childCenter = child.offsetLeft + child.offsetWidth / 2;
          const distance = Math.abs(childCenter - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = i;
          }
        });
        setActiveIndex(closest);
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  return <section className="bg-ink text-white px-4 sm:px-6 py-9 sm:py-12" data-id="element-118" data-mp="131d6xowwc1j">
      <div className="max-w-5xl mx-auto" data-id="element-119" data-mp="3sgxbp7ddik6w">
        <h2 className="font-heading font-extrabold uppercase text-lg sm:text-2xl leading-tight tracking-tight text-center mb-1.5" data-id="element-120" data-mp="3sfvs2eiakpdk">
          <span aria-hidden="true" data-id="element-121" data-mp="34oq9hrkyvizk">👀</span> Look inside Fitness Senior Ready™
        </h2>
        <p className="text-center font-heading font-semibold text-[10px] sm:text-xs uppercase tracking-[0.14em] text-lime mb-6" data-id="element-122" data-mp="2zpvuz1u18gl9">
          Don't just read what's included. See what you'll actually use.
        </p>

        <div className="relative" data-id="element-123" data-mp="18llnirgmluwx">
          <ul ref={trackRef} className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-id="element-124" data-mp="1deepj7kpmp5b">
            {insidePages.map((page, i) => <li key={page.src} className="snap-center shrink-0 w-[78%] sm:w-[58%] lg:w-[42%]" aria-label={`Page ${i + 1} of ${insidePages.length}`} data-id="element-125" data-mp="pososfoow1v7" data-mpi={i}>
                <div className="rounded-xl overflow-hidden border border-white/15 bg-white/[0.04]" data-id="element-126" data-mp="yq0o912xz8qv">
                  <img src={page.src} alt={page.alt} loading={i === 0 ? 'eager' : 'lazy'} className="w-full h-auto object-contain" data-id="element-127" data-mp="256m3g08tzng0" />
                </div>
                <p className="mt-2 text-center text-[10px] sm:text-[11px] font-semibold text-white/55 leading-snug" data-id="element-128" data-mp="2kayrz65ydk5p">
                  {page.caption}
                </p>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Previous page" className="absolute left-0 sm:-left-3 top-[42%] -translate-y-1/2 w-9 h-9 rounded-full bg-ink/85 border border-white/25 flex items-center justify-center text-white hover:bg-green disabled:opacity-0 disabled:pointer-events-none transition-colors duration-150 ease-out" data-id="element-129" data-mp="26hfqncdegheu">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-130" data-mp="1nkoctigmmui6" />
          </button>

          <button type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === insidePages.length - 1} aria-label="Next page" className="absolute right-0 sm:-right-3 top-[42%] -translate-y-1/2 w-9 h-9 rounded-full bg-ink/85 border border-white/25 flex items-center justify-center text-white hover:bg-green disabled:opacity-0 disabled:pointer-events-none transition-colors duration-150 ease-out" data-id="element-131" data-mp="3lwws0x2qagsx">
            <ChevronRightIcon className="w-5 h-5" data-id="element-132" data-mp="3jzoja5u8snpv" />
          </button>
        </div>

        <div className="flex justify-center gap-1.5 mt-3" data-id="element-133" data-mp="3691m4i44r5vw">
          {insidePages.map((page, i) => <button key={page.src} type="button" onClick={() => scrollToIndex(i)} aria-label={`Go to page ${i + 1}`} aria-current={activeIndex === i} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${activeIndex === i ? 'w-6 bg-lime' : 'w-1.5 bg-white/30'}`} data-id="element-134" data-mp="3b0be3pd5ilay" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}